import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getCase,
  getCasesByIds,
  getCasesByWorkspaceId,
  getCaseTree,
  searchCases,
  type ListCasesByWorkspaceIdProps,
} from "@/api/resources/cases/api";
import i18n from "@/providers/i18n";
import { getEntityTreeLeveled } from "@/utils/entityTree";

import type { SearchCasesProps } from "@/api/resources/cases/types";

const entity = i18n.t("case");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("cases");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};
const searchEntities = i18n.t("search-results");
const searchEntitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: searchEntities }),
};

export const casesQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "cases"],
  lists: () => [...casesQueryKeys.all(), "list"],
  listByIds: (ids?: string[]) => [...casesQueryKeys.lists(), "byIds", ids],
  listByWorkspaceId: (props: ListCasesByWorkspaceIdProps) => [...casesQueryKeys.lists(), "byWorkspaceId", props],
  byId: (id?: string) => [...casesQueryKeys.all(), "byId", id],
  // descendant trees (linked cases / maintenances) — a case's name is also shown in maintenance trees
  trees: () => [...casesQueryKeys.all(), "tree"],
  tree: (id?: string) => [...casesQueryKeys.trees(), id],
  search: (props: SearchCasesProps) => [...casesQueryKeys.all(), "search", props],
};

export const casesQueries = {
  listByIds: (ids?: string[]) =>
    queryOptions({
      queryKey: [...casesQueryKeys.listByIds(ids)],
      queryFn: ids ? () => getCasesByIds(ids) : skipToken,
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listByWorkspaceId: ({ workspace_id, params }: ListCasesByWorkspaceIdProps) =>
    queryOptions({
      queryKey: [...casesQueryKeys.listByWorkspaceId({ workspace_id, params })],
      queryFn: workspace_id ? () => getCasesByWorkspaceId({ workspace_id, params: params ?? {} }) : skipToken,
      placeholderData: (prev) => prev, // ! this prevents flashing of results when using search in selectors
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...casesQueryKeys.byId(id)],
      queryFn: id ? () => getCase(id) : skipToken,
      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  // the descendant tree (linked maintenances / cases) as nested, leveled tree
  treeLeveled: (id?: string) =>
    queryOptions({
      queryKey: [...casesQueryKeys.tree(id)],
      queryFn: id ? () => getCaseTree(id) : skipToken,
      select: getEntityTreeLeveled,
      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  search: (props: SearchCasesProps) =>
    queryOptions({
      queryKey: [...casesQueryKeys.search(props)],
      queryFn: props.query ? () => searchCases(props) : skipToken,

      meta: {
        errorMessage: searchEntitiesErrorMessage,
      },
    }),
};
